import { Component, Input, inject, signal } from '@angular/core';
import { ChatMessage } from '../model/search-message-dto';
import { TableModule } from 'primeng/table';
import { TranslatePipe } from '@ngx-translate/core';
import { ComponentBase } from '../../../../../common/component-base';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { DocType } from '../../../../../common/enum/document-type.enum';
import { ButtonModule } from 'primeng/button';
import { LinkifyPipe } from '../../../../../common/pipes/linkify.pipe';
import { NotificationService } from '../../../../../common/service/notification.service';
import { UserService } from '../../../../../common/service/user.service';
import { CustomerService } from '../../../../../common/service/customer.service';
import { Role } from '../../../../auth/role.enum';
import { SearchResultItem } from '../model/search-result-item';

@Component({
  selector: 'gate-search-detail-result',
  standalone: true,
  imports: [TableModule, ButtonModule, TranslatePipe, DatePipe, CurrencyPipe, LinkifyPipe],
  templateUrl: './search-detail-result.component.html',
  styleUrl: './search-detail-result.component.scss'

})
export class SearchDetailResultComponent  extends ComponentBase {
  @Input() public searchMessage!: ChatMessage;
  private userService = inject(UserService);
  private customerService = inject(CustomerService);
  private userProfile = this.userService.getUserProfile();
  public isPdfDownloading = signal<boolean>(false);

  isACPUser() {
    return this.userProfile.roles.includes(Role.AccountManager);
  }

  public get firstDocumentItem(): SearchResultItem | undefined {
    return this.searchMessage.searchResultItems?.find(x => x.documentNo != null);
  }

  public get isPdfArchive(): boolean {
    return !!this.firstDocumentItem?.isPdfArchive;
  }

  public get readOnly(): boolean {
    return !!this.firstDocumentItem?.readOnly;
  }

  public get documentNo(){
    return this.searchMessage.searchResultItems?.find(x => x.documentNo != null)?.documentNo;
  }

  public get orderer(){
    return this.searchMessage.searchResultItems?.find(x => x.orderer != null)?.orderer;
  }

  public get date(){
    if(this.searchMessage && this.isValidDate(this.searchMessage.searchResultItems?.find(x => x.date != null)?.date))
      return this.searchMessage?.searchResultItems?.find(x => x.date != null)?.date;
    return '';
  }

  public get documentType(){
    return this.searchMessage.searchResultItems?.find(x => x.documentType != null && x.documentType>=0)?.documentType;
  }

  private isValidDate(value: any): boolean {
    const regex = /^(?:\d{4}-\d{2}-\d{2}T(?:\d{2}:\d{2}:\d{2}|\d{6}-\d{3}Z)|[A-Za-z]{3} [A-Za-z]{3} \d{2} \d{4} \d{2}:\d{2}:\d{2} [A-Za-z]*[+-]?\d{4} \(.+\))$/;
    return regex.test(value);
  }

   /**
     * Method to get the document URL based on the document type and document number.
     * @param document - The document number.
     * @param documentType - The document type enum.
     * @returns The document URL.
     */
      openDocument(openPdf: boolean = false) {
        if (this.documentNo && this.documentType) {
          const documentNumber = this.documentNo?.replace('/', '_');
          var url: string = '';
          const pdfQuery = openPdf ? '?openPdf=true' : '';
          if (this.documentType == DocType.Offer) url = `/offer/${documentNumber}${pdfQuery}`;
          else if (this.documentType == DocType.OrderConfirmation) url = `/order/${documentNumber}${pdfQuery}`;
          else if (this.documentType == DocType.DeliveryNote) url = `/delivery-note/${documentNumber}${pdfQuery}`;
          else if (this.documentType == DocType.Invoice) url = `/invoice/${documentNumber}${pdfQuery}`;
          if (url) this.navigateToNewTab(url);
        }
      }

  public openPdf() {
    if (!this.documentNo) return;

    if (this.searchMessage.impersonateWith && this.isACPUser()) {
      let customerName: string;
      let customerId = this.searchMessage.impersonateWith;
      this.customerService.getCustomerData(customerId)
        .then((customerData) => {
          customerName = customerData?.fullName;
        }).finally(() => {
          this.userService.startImpersonation({ impersonatedUserSubject: null, customerId: customerId, impersonatedCustomerName: customerName, impersonatedType: 'Customer', initiatorType: 'ACP_User', log: true }).then(() => {
            this.openDocument(true);
          });
        }).catch((error) => {
          console.error('Error fetching customer data for impersonation:', error);
        });
    } else {
      this.openDocument(true);
    }
  }
}
